import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { today } from '@internationalized/date';
import { useDispatch, useSelector } from 'react-redux';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { Dropdown, FormRow } from '../../../common_components/FormInputs';
import CustomDatePicker from '../../../common_components/daterangepicker/CustomDatePicker';
import { toast } from '../../../common_components/Toast/ToastUtils';
import { createAgendaThunk, prepareAgendaThunk, fetchVisibleClients, fetchClientMeetings, clearClientMeetings, selectAgendaVisibleClients, selectAgendaVisibleClientsLoading, selectAgendaSaving, selectAgendaPreparing, selectClientMeetings, selectClientMeetingsLoading, selectClientMeetingsError, } from '../../../redux/meetingAgendaSlice';
import MeetingAccordionList from './MeetingAccordionList';
/** Portal-wide reporting timezone, so the default doesn't shift by viewer. */
const AGENCY_TIMEZONE = 'America/New_York';
/**
 * The reporting period the agenda's performance numbers are built from.
 *
 * Defaults match the Account Overview picker: month-to-date through yesterday
 * (today's data is incomplete), or the whole of last month when today is the
 * 1st, compared month-over-month.
 *
 * The formula is duplicated rather than taken from `useDateRange`, which reads
 * Account Overview's sessionStorage / redux cache / ?month= — in a drawer that
 * would silently inherit whatever dates that module was last left on.
 */
function defaultReportingPeriod() {
    const todayEt = today(AGENCY_TIMEZONE);
    const yesterday = todayEt.subtract({ days: 1 });
    const start = todayEt.day === 1
        ? yesterday.set({ day: 1 }) // 1st → all of last month
        : todayEt.set({ day: 1 }); // otherwise → MTD
    const end = yesterday;
    return {
        main: { start, end },
        compare: {
            start: start.subtract({ months: 1 }),
            end: end.subtract({ months: 1 }),
        },
    };
}
/** CalendarDate → YYYY-MM-DD for the API. */
const toYmd = (d) => `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
const NewAgendaDrawer = ({ open, onClose, defaultClientId = '', onSuccess, onPrepared, }) => {
    const dispatch = useDispatch();
    const visibleClients = useSelector(selectAgendaVisibleClients);
    const clientsLoading = useSelector(selectAgendaVisibleClientsLoading);
    const saving = useSelector(selectAgendaSaving);
    const preparing = !!useSelector(selectAgendaPreparing);
    const meetings = useSelector(selectClientMeetings);
    const meetingsLoading = useSelector(selectClientMeetingsLoading);
    const meetingsError = useSelector(selectClientMeetingsError);
    const [clientId, setClientId] = useState('');
    const [dateRange, setDateRange] = useState(() => defaultReportingPeriod().main);
    const [compareRange, setCompareRange] = useState(() => defaultReportingPeriod().compare);
    const [selectedIds, setSelectedIds] = useState([]);
    const [error, setError] = useState('');
    // Reset per open so a previous pick never leaks into the next agenda, and
    // seed from the listing filter.
    useEffect(() => {
        if (!open) {
            dispatch(clearClientMeetings());
            return;
        }
        setClientId(defaultClientId || '');
        const period = defaultReportingPeriod();
        setDateRange(period.main);
        setCompareRange(period.compare);
        setSelectedIds([]);
        setError('');
        if (!visibleClients.length) {
            dispatch(fetchVisibleClients());
        }
        // visibleClients is intentionally not a dependency — refetching on every
        // list change would clobber the user's selection mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultClientId, dispatch]);
    // Meetings depend on the CLIENT only. The reporting period below drives the
    // performance numbers, not which calls are listed — changing it must never
    // refetch meetings or clear the user's ticks.
    useEffect(() => {
        if (!open)
            return;
        setSelectedIds([]);
        if (!clientId) {
            dispatch(clearClientMeetings());
            return;
        }
        dispatch(fetchClientMeetings({ clientId }));
    }, [open, clientId, dispatch]);
    const clientOptions = useMemo(() => (visibleClients || []).map((c) => ({
        value: String(c._id),
        label: c.name,
    })), [visibleClients]);
    const handleToggleMeeting = useCallback((transcriptId) => {
        setSelectedIds((prev) => prev.includes(transcriptId)
            ? prev.filter((id) => id !== transcriptId)
            : [...prev, transcriptId]);
    }, []);
    /** Creates the agenda row and returns its id, or null if it failed. */
    const createAgenda = async () => {
        if (!clientId) {
            setError('Please select a client.');
            return null;
        }
        const chosen = meetings.filter((m) => selectedIds.includes(m.transcript_id));
        try {
            const res = await dispatch(createAgendaThunk({
                client_id: clientId,
                // Drives the MTD / comparison columns of every performance
                // table in the generated documents.
                period: {
                    start: toYmd(dateRange.start),
                    end: toYmd(dateRange.end),
                    ...(compareRange
                        ? {
                            compare_start: toYmd(compareRange.start),
                            compare_end: toYmd(compareRange.end),
                        }
                        : {}),
                },
                meetings: chosen.map((m) => ({
                    transcript_id: m.transcript_id,
                    title: m.title,
                    date: m.date,
                    participants: m.participants,
                    action_items: m.action_items,
                    short_summary: m.short_summary,
                    bullet_gist: m.bullet_gist,
                })),
            })).unwrap();
            return res.agenda?._id || null;
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Failed to create meeting agenda');
            return null;
        }
    };
    /**
     * The drawer's only action: record the agenda, then generate its documents.
     * The row is created first so a generation failure keeps the client and
     * meeting selection — the list row's "Prepare Agenda" action retries it
     * instead of making the user pick everything again.
     */
    const handlePrepare = async () => {
        const id = await createAgenda();
        if (!id)
            return;
        onSuccess?.();
        try {
            const res = await dispatch(prepareAgendaThunk(id)).unwrap();
            // Queued, not finished — generation runs for minutes in the
            // worker. Close straight away and let the list poll it; holding
            // the drawer open for the duration is what the 504 interrupted.
            toast.info(res.message || 'Preparing the agenda — this takes a few minutes.');
        }
        catch (err) {
            toast.error(typeof err === 'string'
                ? err
                : err?.message || 'Could not start the agenda — retry it from the list.');
        }
        finally {
            onSuccess?.();
            onClose();
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: "New Agenda", width: "md", headerActions: 
        /* A client is all that's required. Meetings are optional —
           Fireflies matches on the client name in the meeting title, so
           a differently-titled or unrecorded call leaves nothing to
           tick, and the documents are still worth generating from the
           performance data alone. */
        clientId ? (_jsx(AppButton, { variant: "contained", onClick: handlePrepare, disabled: saving || preparing, loading: saving || preparing, sx: {
                borderRadius: '8px',
                px: 3,
                bgcolor: '#FF008E',
                '&:hover': { bgcolor: '#E6007A' },
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
            }, children: saving || preparing
                ? 'Preparing…'
                : selectedIds.length > 0
                    ? `Prepare Agenda (${selectedIds.length})`
                    : 'Prepare Agenda' })) : null, children: _jsx(Box, { sx: { p: 1, bgcolor: '#F9FAFB', minHeight: '100%' }, children: _jsxs(Box, { sx: {
                    bgcolor: '#fff',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }, children: [_jsx(FormRow, { columns: 1, gap: 16, children: _jsx(Dropdown, { label: "Client", required: true, value: clientId, onChange: (v) => {
                                setClientId(String(v ?? ''));
                                setError('');
                            }, options: clientOptions, placeholder: clientsLoading ? 'Loading clients…' : 'Select a client', loading: clientsLoading, searchable: clientOptions.length > 10, error: !!error, errorMessage: error }) }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsxs("div", { className: "form-input-wrapper full-width", children: [_jsx("label", { className: "form-input-label", children: "Reporting Period" }), _jsx(CustomDatePicker, { value: dateRange, compareValue: compareRange, onApply: ({ mainRange, compareRange: cmp }) => {
                                        if (mainRange)
                                            setDateRange(mainRange);
                                        setCompareRange(cmp || null);
                                    }, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", maxPastYears: 2, disabled: preparing, popperStrategy: "fixed" })] }) }), _jsxs(Box, { children: [_jsxs(Typography, { sx: {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: '#344054',
                                    mb: 1,
                                }, children: ["Meetings", selectedIds.length > 0 && (_jsxs(Typography, { component: "span", sx: { fontSize: 12, color: '#667085', ml: 0.5 }, children: ["(", selectedIds.length, " selected)"] }))] }), preparing && (_jsxs(Typography, { sx: {
                                    fontSize: 12,
                                    color: '#B54708',
                                    bgcolor: '#FFFAEB',
                                    border: '1px solid #FEDF89',
                                    borderRadius: '8px',
                                    px: 1.5,
                                    py: 1,
                                    mb: 1,
                                }, children: ["Building the Internal Worksheet and Client Agenda from", ' ', selectedIds.length > 0
                                        ? 'the selected calls and this period’s performance data'
                                        : 'this period’s performance data', ' ', "\u2014 generation runs in the background and takes a few minutes. You can close this and watch the row in the list."] })), _jsx(MeetingAccordionList, { meetings: meetings, selectedIds: selectedIds, onToggle: handleToggleMeeting, loading: meetingsLoading, error: meetingsError, idle: !clientId })] })] }) }) }));
};
export default NewAgendaDrawer;
